import React from 'react';

// Estado vacío compartido de VLS: "no hay nada que mostrar" con la misma
// cara en todas las apps (ícono opcional, título, texto y una acción).
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES:
//     <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
//       No hay secciones para mostrar en esta vista.
//     </div>
//   DESPUÉS:
//     <EmptyState framed title="No hay secciones para mostrar en esta vista." />
//
//   Con acción:
//     <EmptyState icon={<Users />} title="Sin colaboradores"
//       description="Agregue el primero para empezar."
//       action={<Button onClick={abrir}>Agregar colaborador</Button>} />
//   o, sin Button propio: actionLabel="Agregar" onAction={abrir}.
//
// Props: icon (nodo, p. ej. un ícono de lucide), title, description (o
// children), action (nodo) | actionLabel + onAction, size 'md' | 'sm'
// (compacto, para dentro de una tabla o tarjeta), framed (borde y fondo de
// tarjeta), className (solo layout), as (etiqueta raíz, 'div' por defecto).
// Sin dependencias; colores por var(--color-*) con respaldo.

const C = {
  card: 'var(--color-card, #FFFFFF)',
  fg: 'var(--color-foreground, #1C1917)',
  muted: 'var(--color-muted, #F5F5F4)',
  mutedFg: 'var(--color-muted-foreground, #78716C)',
  border: 'var(--color-border, #E7E5E4)',
  primary: 'var(--color-primary, #0F7A3D)',
};

export function BotonSecundario({ children, onClick, disabled, busy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className="inline-flex items-center justify-center text-sm font-medium rounded-lg border hover:bg-black/5 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ minHeight: 44, padding: '0 16px', gap: 8, borderColor: C.border, background: C.card, color: C.fg }}
    >
      {children}
    </button>
  );
}

export default function EmptyState({
  icon, title, description, children, action, actionLabel, onAction,
  size = 'md', framed = false, className = '', as: Tag = 'div', 'data-testid': testId,
}) {
  const sm = size === 'sm';
  const texto = description ?? children;
  const accion = action ?? (actionLabel && onAction ? <BotonSecundario onClick={onAction}>{actionLabel}</BotonSecundario> : null);
  return (
    <Tag
      role="status"
      data-vls-empty=""
      data-testid={testId}
      className={`flex flex-col items-center text-center ${framed ? 'border rounded-xl' : ''} ${className}`}
      style={{
        gap: sm ? 6 : 10,
        padding: sm ? '16px 12px' : '32px 16px',
        ...(framed ? { background: C.card, borderColor: C.border } : null),
      }}
    >
      {icon && (
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center rounded-full"
          style={{ width: sm ? 36 : 48, height: sm ? 36 : 48, background: C.muted, color: C.mutedFg, marginBottom: sm ? 0 : 4 }}
        >
          {icon}
        </span>
      )}
      {title && (
        <p className={sm ? 'text-sm font-medium' : 'text-base font-semibold'} style={{ margin: 0, color: sm ? C.mutedFg : C.fg, maxWidth: '40ch' }}>
          {title}
        </p>
      )}
      {texto && (
        <div className="text-sm" style={{ color: C.mutedFg, maxWidth: '48ch' }}>{texto}</div>
      )}
      {accion && <div style={{ marginTop: sm ? 4 : 8 }}>{accion}</div>}
    </Tag>
  );
}
